import {
  BlockStack,
  Banner,
  Button,
  Card,
  InlineStack,
  Text,
} from "@shopify/polaris";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  applyLeadTimeSuggestion,
  listLeadTimeSuggestions,
  type LeadTimeApplyResult,
} from "../api/reorder_policies";
import { t } from "../lib/i18n";

export const LEAD_TIME_SUGGESTION_QUERY_KEY = ["lead-time-suggestions"];

interface Props {
  /** 採用後に発注点一覧を引き直させる（親が持つクエリキー）。 */
  onApplied: () => void;
  disabled?: boolean;
}

function applySummary(result: LeadTimeApplyResult): string {
  const parts = [
    t("{n}件にリードタイムを設定しました。").replace(
      "{n}",
      String(result.updated),
    ),
  ];
  if (result.kept_existing > 0) {
    parts.push(
      t("既に入力済みの{n}件はそのままにしました。").replace(
        "{n}",
        String(result.kept_existing),
      ),
    );
  }
  if (result.inert > 0) {
    // 「書いた件数」だけ見せると効果を過大に見せる＝閾値が動かない件数も必ず出す。
    parts.push(
      t("うち{n}件は在庫日数の目標が未設定（または発注点を手入力済み）のため、発注点はまだ変わりません。").replace("{n}", String(result.inert)),
    );
  }
  return parts.join(" ");
}

/**
 * 入荷実績から出した仕入先別リードタイムの提示。
 *
 * 自動では入れない＝マーチャントが押した時点で発注点ポリシーへコピーされる。
 * 提示が無いとき（実績不足・埋める商品が無い・プラン外の403）は何も描画しない
 * ＝根拠の無い数字で空欄を埋めないし、押しても何も起きないボタンも出さない。
 */
export function LeadTimeSuggestionCard({ onApplied, disabled }: Props) {
  const queryClient = useQueryClient();
  const [result, setResult] = useState<LeadTimeApplyResult | null>(null);

  const suggestionsQuery = useQuery({
    queryKey: LEAD_TIME_SUGGESTION_QUERY_KEY,
    queryFn: listLeadTimeSuggestions,
    retry: false,
  });

  const apply = useMutation({
    mutationFn: applyLeadTimeSuggestion,
    onSuccess: (applied) => {
      setResult(applied);
      queryClient.invalidateQueries({ queryKey: LEAD_TIME_SUGGESTION_QUERY_KEY });
      onApplied();
    },
  });

  const suggestions = suggestionsQuery.data ?? [];

  if (result) {
    return (
      <Banner
        tone={result.inert > 0 ? "warning" : "success"}
        title={t("実測リードタイムを採用しました")}
        onDismiss={() => setResult(null)}
      >
        <p>{applySummary(result)}</p>
      </Banner>
    );
  }

  // 実績が足りない／埋めるものが無い／プラン外（403）はすべて「静かに出さない」。
  if (suggestionsQuery.isLoading || suggestionsQuery.isError) return null;
  if (suggestions.length === 0) return null;

  return (
    <Card>
      <BlockStack gap="300">
        <BlockStack gap="100">
          <Text as="h2" variant="headingMd">
            {t("入荷実績からリードタイムを提案")}
          </Text>
          <Text as="p" tone="subdued">
            {t("これまでの発注から入荷までの実測平均です。採用するとその仕入先の商品にまとめて設定され、発注点の自動計算が効きます。")}
          </Text>
        </BlockStack>

        {suggestions.map((suggestion) => (
          <InlineStack
            key={suggestion.supplier_id}
            align="space-between"
            blockAlign="center"
            gap="300"
            wrap
          >
            <BlockStack gap="050">
              <Text as="span" fontWeight="semibold">
                {suggestion.supplier_name ?? t("名称未設定の仕入先")}
              </Text>
              <Text as="span" tone="subdued">
                {t("実測平均 {days}日（発注{count}件）／未設定の商品 {fillable}件")
                  .replace("{days}", String(suggestion.avg_lead_time_days))
                  .replace("{count}", String(suggestion.sample_size))
                  .replace("{fillable}", String(suggestion.fillable_variant_count))}
              </Text>
            </BlockStack>
            <Button
              onClick={() => apply.mutate(suggestion.supplier_id)}
              loading={apply.isPending && apply.variables === suggestion.supplier_id}
              disabled={disabled || apply.isPending}
            >
              {t("採用する")}
            </Button>
          </InlineStack>
        ))}

        {apply.isError && (
          <Banner tone="critical" title={t("採用に失敗しました")}>
            <p>{(apply.error as Error).message}</p>
          </Banner>
        )}
      </BlockStack>
    </Card>
  );
}
